// Guided UI-rondleiding via driver.js. Twee talen (NL/EN). Multi-page:
// navigeert automatisch tussen schermen via onNextClick + localStorage state.
//
// Flow:
//   Dashboard → /project/new → /month → /ploegen → terug naar dashboard
//
// State (active, segmentIndex, lang) zit in localStorage zodat de tour
// overleeft tussen page-loads. AppShell roept resumeIfActive() bij elke
// route-change zodat de juiste segment vanzelf doorloopt.
import { driver, type Config, type DriveStep, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';

export type TourLang = 'nl' | 'en';

const LANG_KEY = 'recotex_tour_lang';
const STATE_KEY = 'recotex_tour_state';

interface TourState {
  active: boolean;
  segmentIndex: number;
  lang: TourLang;
}

export function getTourLang(): TourLang {
  if (typeof window === 'undefined') return 'nl';
  return localStorage.getItem(LANG_KEY) === 'en' ? 'en' : 'nl';
}

export function setTourLang(lang: TourLang): void {
  localStorage.setItem(LANG_KEY, lang);
}

function getState(): TourState | null {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(STATE_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw) as TourState; } catch { return null; }
}

function setState(s: TourState): void {
  localStorage.setItem(STATE_KEY, JSON.stringify(s));
}

function clearState(): void {
  localStorage.removeItem(STATE_KEY);
}

// === Translations ===

interface T {
  next: string; prev: string; done: string; progress: string;
  // Section navigators
  nextSection: string;
  endTour: string;
  // Dashboard
  welcomeTitle: string; welcomeBody: string;
  dashTitle: string; dashBody: string;
  dashNewBtnTitle: string; dashNewBtnBody: string;
  dashFiltersTitle: string; dashFiltersBody: string;
  dashViewTitle: string; dashViewBody: string;
  dashNavTitle: string; dashNavBody: string;
  dashGoNewTitle: string; dashGoNewBody: string;
  // New project
  newTitle: string; newBody: string;
  newUploadTitle: string; newUploadBody: string;
  newAdvancedTitle: string; newAdvancedBody: string;
  newAfterTitle: string; newAfterBody: string;
  newStep2Title: string; newStep2Body: string;
  newStep2ItemsTitle: string; newStep2ItemsBody: string;
  newStep2NotesTitle: string; newStep2NotesBody: string;
  newPloegFailTitle: string; newPloegFailBody: string;
  newPloegFailBannerTitle: string; newPloegFailBannerBody: string;
  newPloegFailDropdownTitle: string; newPloegFailDropdownBody: string;
  newPloegFailButtonTitle: string; newPloegFailButtonBody: string;
  newProjFailTitle: string; newProjFailBody: string;
  newProjFailFieldTitle: string; newProjFailFieldBody: string;
  newToStep3Title: string; newToStep3Body: string;
  newStep3Title: string; newStep3Body: string;
  newStep3PendingTitle: string; newStep3PendingBody: string;
  newStep3AutoTitle: string; newStep3AutoBody: string;
  newStep3ChargesTitle: string; newStep3ChargesBody: string;
  newStep3SaveTitle: string; newStep3SaveBody: string;
  newGoMonthTitle: string; newGoMonthBody: string;
  // Month overview
  monthTitle: string; monthBody: string;
  monthPeriodTitle: string; monthPeriodBody: string;
  monthSubcoTitle: string; monthSubcoBody: string;
  monthSendTitle: string; monthSendBody: string;
  monthGoPloegenTitle: string; monthGoPloegenBody: string;
  // Ploegen
  ploegenTitle: string; ploegenBody: string;
  ploegenAutoTitle: string; ploegenAutoBody: string;
  ploegenColTitle: string; ploegenColBody: string;
  ploegenInfoTitle: string; ploegenInfoBody: string;
  ploegenGoEndTitle: string; ploegenGoEndBody: string;
  // End
  endTitle: string; endBody: string;
}

const NL: T = {
  next: 'Volgende →', prev: '← Vorige', done: 'Klaar', progress: '{{current}} / {{total}}',
  nextSection: 'Volgende sectie →', endTour: 'Tour afsluiten',
  welcomeTitle: '👋 Welkom bij Recotex Calculaties',
  welcomeBody: 'Korte rondleiding van ~3 minuten doorheen alle hoofdschermen. Klik "Volgende" om te starten, of "ESC" om te stoppen.',
  dashTitle: '🏠 Dashboard', dashBody: 'Dit is je startpagina. Hier zie je álle projecten: gefilterd, gesorteerd en doorzoekbaar.',
  dashNewBtnTitle: '➕ Nieuw project starten', dashNewBtnBody: 'Klik hier om een opstartfiche-PDF te uploaden. AI + Bouwflow lezen alles automatisch uit.',
  dashFiltersTitle: '🔍 Zoeken & filteren', dashFiltersBody: 'Zoek op klantnaam, projectnummer of bestand. Filter ploeg/status. Klik "Filters" voor datum + bedrag-bereik.',
  dashViewTitle: '👁 Kaarten vs Tabel', dashViewBody: 'Wissel tussen kaart-weergave (visueel) en tabel-weergave (compacter, sorteerbaar per kolom).',
  dashNavTitle: '📂 Zijmenu', dashNavBody: 'Dashboard · Nieuw project · Maandoverzicht · Prijslijsten · Onderaannemers · Gebruikers · Activiteit · Account. Admin-only secties zien enkel admins.',
  dashGoNewTitle: '▶ Op naar Nieuw Project', dashGoNewBody: 'We gaan nu naar het scherm waar je nieuwe projecten aanmaakt. Klik "Volgende" om te navigeren.',
  newTitle: '📄 Nieuw project', newBody: 'Eén upload — de AI parseert, koppelt items aan je prijslijst, en presenteert een review-scherm.',
  newUploadTitle: '⬆ Upload de PDF', newUploadBody: 'Klik of sleep een opstartfiche-PDF (max 10MB). Ploeg + projectnummer worden automatisch herkend via AI + Bouwflow.',
  newAdvancedTitle: '⚙ Geavanceerd (optioneel)', newAdvancedBody: 'Zeldzaam — kan je ploeg of projectnummer vooraf vastleggen. Meestal niet nodig.',
  newAfterTitle: '🤖 We simuleren een upload', newAfterBody: 'Klik "Volgende" — we vullen demo-data in zodat je stap 2 en stap 3 kan zien zonder echt te uploaden.',
  newStep2Title: '✏ Stap 2 — project + items review', newStep2Body: 'AI parseert in 5-15s. Project-info (klantnaam, adres, projectnummer, adviseur) en geparseerde items komen vooringevuld terug.',
  newStep2ItemsTitle: '📋 Items-tabel', newStep2ItemsBody: 'Elke regel is een werkpost uit de PDF. Pas geadviseerde of werkelijke hoeveelheid aan. Met "+ Item handmatig" voeg je toe wat AI gemist heeft.',
  newStep2NotesTitle: '⚠ Opmerkingen-blok', newStep2NotesBody: 'Vrije tekst uit de fiche (bv. "Stelling enkel voor gevel", "Klant wil ook trespa"). Lees aandachtig — bevat vaak prijsregelingen of speciale instructies die niet in de werkpost-tabel staan.',
  newPloegFailTitle: '🚨 Wat als AI de ploeg niet vindt?', newPloegFailBody: 'Klik "Volgende" en ik simuleer dat scenario. Een gele waarschuwingsbanner verschijnt bovenaan met opties om handmatig te koppelen.',
  newPloegFailBannerTitle: '⚠ Waarschuwingsbanner', newPloegFailBannerBody: 'Hier zie je: "Bouwflow toont \'DEMO {Edrin}\' als ploeg op deze opstartfiche". Bouwflow weet de juiste crew, maar die is nog niet aan een lokale ploeg gekoppeld.',
  newPloegFailDropdownTitle: '🔗 Koppel-dropdown in de banner', newPloegFailDropdownBody: 'Eén klik in deze dropdown koppelt het Bouwflow-id aan de juiste lokale ploeg én voert de matching uit. Volgende keer dat ploeg op een fiche staat: auto-detect werkt. Geen Bouwflow-info? Dan staat hier de gewone ploeg-dropdown.',
  newPloegFailButtonTitle: '🚫 "Volgende" is disabled', newPloegFailButtonBody: 'Zolang er geen ploeg geselecteerd is, blijft de knop "Volgende: matching →" onderaan disabled. Voorkomt dat je per ongeluk doorgaat zonder ploeg.',
  newProjFailTitle: '🚨 Wat als AI het projectnummer niet vindt?', newProjFailBody: 'Klik "Volgende" — ik laat het projectnummer-veld leeg zoals het zou zijn als AI niets vond.',
  newProjFailFieldTitle: '📝 Projectnummer-veld', newProjFailFieldBody: 'Typ hier het projectnummer (bv. "1842"). Het "P-" prefix wordt automatisch toegevoegd. Voor opname in maandoverzicht is dit aanbevolen maar niet verplicht — een leeg projectnummer is ook OK.',
  newToStep3Title: '▶ Naar matching review', newToStep3Body: 'Klik "Volgende" — we springen naar stap 3 waar items aan de prijslijst gekoppeld worden.',
  newStep3Title: '🔗 Stap 3 — matching review', newStep3Body: 'AI heeft elke PDF-werkpost gematcht met een lijn uit de prijslijst van deze ploeg. Hoog-zekere matches staan rechts onder "Gematcht"; items zonder zekere match komen in een review-queue bovenaan.',
  newStep3PendingTitle: '⏳ Drie keuzes per review-item', newStep3PendingBody: 'Voor elk item in de queue heb je 3 knoppen:\n\n• ✓ Match — kies de juiste arbeid uit de prijslijst-dropdown en bevestig. Wordt opgeslagen + onthouden voor toekomstige PDFs ("learned matches").\n\n• + Meerwerk — geef een eenheidsprijs en het item komt in de "Meerwerk"-sectie (extra-werk dat de onderaannemer mag factureren).\n\n• Negeer — sluit dit item uit (bv. footers, lege rijen, niet-relevant). Verdwijnt uit de calculatie.',
  newStep3AutoTitle: '✓ Gematcht — controleer kort', newStep3AutoBody: 'Auto-gematchte regels staan eronder. Kijk de prijs + hoeveelheid na. Vul subitems uit als de match samengevoegd werd. Klik op de prullenbak om een match te verwijderen als AI fout zat.',
  newStep3ChargesTitle: '➖ Aanrekening (optioneel)', newStep3ChargesBody: 'Onderaan kan je SCHADE, INHOUDING, BOETE of ANDERE-kosten aftrekken van het totaal. Wordt op de factuur als negatieve regel getoond zodat onderaannemer ziet wat er ingehouden werd en waarom.',
  newStep3SaveTitle: '💾 Concept of Goedkeuren?', newStep3SaveBody: '"Concept opslaan" → status "Concept", je kan later verder werken. "Goedkeuren" → status "Goedgekeurd", verschijnt in maandoverzicht voor facturatie. Beide bewaren de calculatie + leren handmatige matches.',
  newGoMonthTitle: '▶ Op naar Maandoverzicht', newGoMonthBody: 'Eens projecten goedgekeurd zijn groepeer je ze per onderaannemer per maand voor facturatie + WhatsApp/email.',
  monthTitle: '📅 Maandoverzicht', monthBody: 'Per onderaannemer per maand: aantal projecten, totaalbedrag, exports voor facturatie.',
  monthPeriodTitle: '🗓 Periode kiezen', monthPeriodBody: 'Selecteer jaar + maand. Sorteer op totaal, aantal, of alfabetisch op ploeg/firma.',
  monthSubcoTitle: '👷 Eén onderaannemer focussen', monthSubcoBody: 'Pick een specifieke ploeg om enkel hun cijfers te zien. Detail-PDF wordt dan beschikbaar.',
  monthSendTitle: '📤 Doormailen + WhatsApp', monthSendBody: 'Eens een ploeg geselecteerd is, verschijnen "Doormailen" (email + PDF) en "WhatsApp" (Cloud API + PDF) knoppen.',
  monthGoPloegenTitle: '▶ Op naar Onderaannemers', monthGoPloegenBody: 'Daar beheer je ploegen, contact-info, prijslijsten en Bouwflow-koppelingen.',
  ploegenTitle: '👷 Onderaannemers', ploegenBody: 'Ploegen-beheer met inline-edit. Klik op een veld om te bewerken — auto-save bij verlaten van het veld.',
  ploegenAutoTitle: '⇄ Bouwflow auto-koppelen', ploegenAutoBody: 'Eén klik en de app vindt elke ploeg in Bouwflow, koppelt automatisch en vult email + telefoon in.',
  ploegenColTitle: '🔢 Bouwflow-kolom', ploegenColBody: 'Het nummer hier is de subcontractor-group ID uit Bouwflow. Maakt auto-detect van ploeg bij PDF-upload mogelijk.',
  ploegenInfoTitle: 'ℹ Uitleg & FAQ', ploegenInfoBody: 'Klap open voor uitgebreide uitleg over Bouwflow-koppeling: wat het doet, hoe een nieuwe ploeg toe te voegen, wat er gebeurt zonder koppeling.',
  ploegenGoEndTitle: '▶ Bijna klaar', ploegenGoEndBody: 'We sluiten de tour af.',
  endTitle: '🎉 Rondleiding afgerond!',
  endBody: 'Je kent nu alle hoofdschermen. Tip: typ "?" voor een snel zoekvenster, klik op je naam rechtsboven om uit te loggen. Voor extra hulp klik altijd opnieuw 📚 Rondleiding.',
};

const EN: T = {
  next: 'Next →', prev: '← Back', done: 'Done', progress: '{{current}} / {{total}}',
  nextSection: 'Next section →', endTour: 'End tour',
  welcomeTitle: '👋 Welcome to Recotex Calculaties',
  welcomeBody: 'A ~3-minute walkthrough of all main screens. Click "Next" to start, or "ESC" to stop.',
  dashTitle: '🏠 Dashboard', dashBody: 'Your home screen. Every project lives here: filtered, sorted, searchable.',
  dashNewBtnTitle: '➕ Start a new project', dashNewBtnBody: 'Click here to upload a startup sheet PDF. AI + Bouwflow extract everything automatically.',
  dashFiltersTitle: '🔍 Search & filter', dashFiltersBody: 'Search by customer, project number or filename. Filter team/status. Click "Filters" for date + amount ranges.',
  dashViewTitle: '👁 Cards vs Table', dashViewBody: 'Toggle between card view (visual) and table view (compact, sortable per column).',
  dashNavTitle: '📂 Side menu', dashNavBody: 'Dashboard · New project · Month overview · Pricelists · Subcontractors · Users · Activity · Account. Admin-only sections only visible to admins.',
  dashGoNewTitle: '▶ Onward to New Project', dashGoNewBody: 'We\'ll now jump to the screen where you create new projects. Click "Next" to navigate.',
  newTitle: '📄 New project', newBody: 'One upload — AI parses, links items to your pricelist, and presents a review screen.',
  newUploadTitle: '⬆ Upload the PDF', newUploadBody: 'Click or drop a startup sheet PDF (max 10MB). Team + project number are auto-detected via AI + Bouwflow.',
  newAdvancedTitle: '⚙ Advanced (optional)', newAdvancedBody: 'Rare — you can pre-set team or project number here. Usually not needed.',
  newAfterTitle: '🤖 Let\'s simulate an upload', newAfterBody: 'Click "Next" — we\'ll fill in demo data so you can see steps 2 and 3 without actually uploading.',
  newStep2Title: '✏ Step 2 — project + items review', newStep2Body: 'AI parses in 5-15s. Project info (customer, address, project number, advisor) and parsed items come pre-filled.',
  newStep2ItemsTitle: '📋 Items table', newStep2ItemsBody: 'Each row is a work item from the PDF. Adjust suggested or actual quantities. Use "+ Add item manually" for anything AI missed.',
  newStep2NotesTitle: '⚠ Notes block', newStep2NotesBody: 'Free-form text from the sheet (e.g. "Scaffolding only for facade", "Customer also wants trespa"). Read carefully — often contains price arrangements or special instructions that aren\'t in the work-item table.',
  newPloegFailTitle: '🚨 What if AI doesn\'t find the team?', newPloegFailBody: 'Click "Next" and I\'ll simulate that scenario. A yellow warning banner appears at the top with manual linking options.',
  newPloegFailBannerTitle: '⚠ Warning banner', newPloegFailBannerBody: 'Here you see: "Bouwflow shows \'DEMO {Edrin}\' as team on this startup sheet". Bouwflow knows the right crew, but it isn\'t linked to a local team yet.',
  newPloegFailDropdownTitle: '🔗 Link dropdown in the banner', newPloegFailDropdownBody: 'One click in this dropdown links the Bouwflow ID to the right local team AND runs the matching. Next time that team appears on a sheet: auto-detect works. No Bouwflow info? Then this becomes a regular team dropdown.',
  newPloegFailButtonTitle: '🚫 "Next" is disabled', newPloegFailButtonBody: 'As long as no team is selected, the "Next: matching →" button at the bottom stays disabled. Prevents accidentally moving forward without a team.',
  newProjFailTitle: '🚨 What if AI doesn\'t find the project number?', newProjFailBody: 'Click "Next" — I\'ll leave the project number field empty as it would be if AI found nothing.',
  newProjFailFieldTitle: '📝 Project number field', newProjFailFieldBody: 'Type the project number here (e.g. "1842"). The "P-" prefix is added automatically. Recommended but not required for month overview — an empty project number is also fine.',
  newToStep3Title: '▶ Onward to matching review', newToStep3Body: 'Click "Next" — we\'ll jump to step 3 where items link to the pricelist.',
  newStep3Title: '🔗 Step 3 — matching review', newStep3Body: 'AI has matched each PDF work item to a line from this team\'s pricelist. High-confidence matches show on the right under "Matched"; uncertain items go in a review queue at the top.',
  newStep3PendingTitle: '⏳ Three choices per review item', newStep3PendingBody: 'For each item in the queue, three buttons:\n\n• ✓ Match — pick the right labor from the pricelist dropdown and confirm. Saved + remembered for future PDFs ("learned matches").\n\n• + Extra — set a unit price and the item moves to the "Extra work" section (extra work the subcontractor can invoice).\n\n• Skip — exclude this item (e.g. footers, empty rows, irrelevant). Disappears from the calculation.',
  newStep3AutoTitle: '✓ Matched — quick check', newStep3AutoBody: 'Auto-matched lines below. Verify price + quantity. Expand subitems if matches were merged. Trash icon removes a match if AI got it wrong.',
  newStep3ChargesTitle: '➖ Deductions (optional)', newStep3ChargesBody: 'At the bottom you can subtract DAMAGE, HOLD, FINE or OTHER costs from the total. Shows as a negative line on the invoice so subcontractor sees what was withheld and why.',
  newStep3SaveTitle: '💾 Draft or Approve?', newStep3SaveBody: '"Save draft" → status "Draft", you can continue editing later. "Approve" → status "Approved", appears in month overview for invoicing. Both save the calculation + learn manual matches.',
  newGoMonthTitle: '▶ Onward to Month Overview', newGoMonthBody: 'Once projects are approved, group them per subcontractor per month for invoicing + WhatsApp/email.',
  monthTitle: '📅 Month overview', monthBody: 'Per subcontractor per month: project count, total amount, exports for invoicing.',
  monthPeriodTitle: '🗓 Pick period', monthPeriodBody: 'Select year + month. Sort by total, count, or alphabetically by team/company.',
  monthSubcoTitle: '👷 Focus one subcontractor', monthSubcoBody: 'Pick a specific team to see only their numbers. Detail PDF becomes available.',
  monthSendTitle: '📤 Send + WhatsApp', monthSendBody: 'Once a team is selected, "Send" (email + PDF) and "WhatsApp" (Cloud API + PDF) buttons appear.',
  monthGoPloegenTitle: '▶ Onward to Subcontractors', monthGoPloegenBody: 'That\'s where you manage teams, contact info, pricelists and Bouwflow linking.',
  ploegenTitle: '👷 Subcontractors', ploegenBody: 'Team management with inline editing. Click any field to edit — auto-saves when you leave the field.',
  ploegenAutoTitle: '⇄ Bouwflow auto-link', ploegenAutoBody: 'One click — the app finds each team in Bouwflow, auto-links and fills email + phone.',
  ploegenColTitle: '🔢 Bouwflow column', ploegenColBody: 'The number here is the subcontractor-group ID from Bouwflow. Enables auto-detect of the team when uploading a PDF.',
  ploegenInfoTitle: 'ℹ Help & FAQ', ploegenInfoBody: 'Expand for detailed explanation of Bouwflow linking: what it does, how to add a new team, what happens without it.',
  ploegenGoEndTitle: '▶ Almost there', ploegenGoEndBody: 'We\'ll wrap up the tour.',
  endTitle: '🎉 Tour complete!',
  endBody: 'You now know all the main screens. Tip: click your name top right to log out. For more help, click 📚 Tour again anytime.',
};

const STRINGS: Record<TourLang, T> = { nl: NL, en: EN };

// === Segments — each segment is one page of the tour ===

interface Segment {
  page: string; // URL path the segment lives on
  build: (s: T, isLast: boolean, nextLabel: string) => DriveStep[];
  next?: string; // URL path to navigate to after this segment
  /** Action gefireed wanneer gebruiker "Volgende" klikt op deze step-index.
   *  Geeft de tour de mogelijkheid om de ProjectNew-state te muteren (demo
   *  activate/deactivate) voordat de volgende step rendert. */
  stepActions?: Record<number, () => void>;
}

// === Tour-demo bridge — window-globals geset door ProjectNew ===

interface TourDemoBridge {
  activateDemo: () => void;
  simulateNoPloeg: () => void;
  restoreDemoPloeg: () => void;
  simulateNoProjectNummer: () => void;
  restoreProjectNummer: () => void;
  goToStep3: () => void;
  deactivateDemo: () => void;
}

function getDemoBridge(): TourDemoBridge | null {
  return (window as unknown as { __recotexTour?: TourDemoBridge }).__recotexTour ?? null;
}

const SEGMENTS: Segment[] = [
  // 1. Dashboard — getrimd: welkom + dashboard intro + nieuw-project knop + zijmenu + transition
  {
    page: '/',
    next: '/project/new',
    build: (s, _isLast, nextLabel) => [
      { popover: { title: s.welcomeTitle, description: s.welcomeBody } },
      { popover: { title: s.dashTitle, description: s.dashBody } },
      { element: 'a[href="/project/new"].btn-accent', popover: { title: s.dashNewBtnTitle, description: s.dashNewBtnBody, side: 'bottom', align: 'end' } },
      { element: 'aside nav ul', popover: { title: s.dashNavTitle, description: s.dashNavBody, side: 'right' } },
      { popover: { title: s.dashGoNewTitle, description: s.dashGoNewBody, popoverClass: 'recotex-tour recotex-tour-cta', nextBtnText: nextLabel } },
    ],
  },
  // 2. New project — diepgaand met fake upload + AI parse demo + interactieve edge-cases
  // Indexes (totaal 19, 0-18):
  //   0: intro
  //   1: upload area
  //   2: "simuleer upload"            → activateDemo
  //   3: stap-2 projectgegevens
  //   4: items-tabel
  //   5: opmerkingen
  //   6: "demo ploeg niet gevonden"   → simulateNoPloeg
  //   7: banner
  //   8: koppel-dropdown
  //   9: disabled volgende-knop       → restoreDemoPloeg
  //  10: "demo projectnr niet gevonden" → simulateNoProjectNummer
  //  11: projectnr-veld                → restoreProjectNummer
  //  12: "naar stap 3"                → goToStep3
  //  13: stap-3 intro
  //  14: pending queue (3 keuzes)
  //  15: auto-gematcht
  //  16: aanrekening
  //  17: concept vs goedkeuren
  //  18: naar Maand                    → deactivateDemo
  {
    page: '/project/new',
    next: '/month',
    stepActions: {
      2: () => getDemoBridge()?.activateDemo(),
      6: () => getDemoBridge()?.simulateNoPloeg(),
      9: () => getDemoBridge()?.restoreDemoPloeg(),
      10: () => getDemoBridge()?.simulateNoProjectNummer(),
      11: () => getDemoBridge()?.restoreProjectNummer(),
      12: () => getDemoBridge()?.goToStep3(),
      18: () => getDemoBridge()?.deactivateDemo(),
    },
    build: (s, _isLast, nextLabel) => [
      { popover: { title: s.newTitle, description: s.newBody } },
      { element: 'label[class*="border-dashed"]', popover: { title: s.newUploadTitle, description: s.newUploadBody, side: 'bottom' } },
      { popover: { title: s.newAfterTitle, description: s.newAfterBody, popoverClass: 'recotex-tour recotex-tour-cta' } },
      { element: '.card', popover: { title: s.newStep2Title, description: s.newStep2Body, side: 'bottom' } },
      { element: 'table', popover: { title: s.newStep2ItemsTitle, description: s.newStep2ItemsBody, side: 'top' } },
      { element: '.card.border-l-4', popover: { title: s.newStep2NotesTitle, description: s.newStep2NotesBody, side: 'top' } },
      { popover: { title: s.newPloegFailTitle, description: s.newPloegFailBody, popoverClass: 'recotex-tour recotex-tour-cta' } },
      { element: 'div.card.border-warning', popover: { title: s.newPloegFailBannerTitle, description: s.newPloegFailBannerBody, side: 'bottom' } },
      { element: 'div.card.border-warning select', popover: { title: s.newPloegFailDropdownTitle, description: s.newPloegFailDropdownBody, side: 'bottom' } },
      { element: '.sticky.bottom-0 button.btn-primary', popover: { title: s.newPloegFailButtonTitle, description: s.newPloegFailButtonBody, side: 'top' } },
      { popover: { title: s.newProjFailTitle, description: s.newProjFailBody, popoverClass: 'recotex-tour recotex-tour-cta' } },
      { element: 'input[placeholder*="P-"]', popover: { title: s.newProjFailFieldTitle, description: s.newProjFailFieldBody, side: 'bottom' } },
      { popover: { title: s.newToStep3Title, description: s.newToStep3Body, popoverClass: 'recotex-tour recotex-tour-cta' } },
      { popover: { title: s.newStep3Title, description: s.newStep3Body } },
      { element: '.space-y-2 > div:first-child', popover: { title: s.newStep3PendingTitle, description: s.newStep3PendingBody, side: 'bottom' } },
      { element: 'table.w-full tbody tr', popover: { title: s.newStep3AutoTitle, description: s.newStep3AutoBody, side: 'top' } },
      { popover: { title: s.newStep3ChargesTitle, description: s.newStep3ChargesBody } },
      { element: '.sticky.bottom-0', popover: { title: s.newStep3SaveTitle, description: s.newStep3SaveBody, side: 'top' } },
      { popover: { title: s.newGoMonthTitle, description: s.newGoMonthBody, popoverClass: 'recotex-tour recotex-tour-cta', nextBtnText: nextLabel } },
    ],
  },
  // 3. Month overview
  {
    page: '/month',
    next: '/',
    build: (s, _isLast, nextLabel) => [
      { popover: { title: s.monthTitle, description: s.monthBody } },
      { element: '.card-flat', popover: { title: s.monthPeriodTitle, description: s.monthPeriodBody, side: 'bottom' } },
      { popover: { title: s.monthSendTitle, description: s.monthSendBody, popoverClass: 'recotex-tour recotex-tour-cta', nextBtnText: nextLabel } },
    ],
  },
  // 4. Final
  {
    page: '/',
    build: (s) => [
      { popover: { title: s.endTitle, description: s.endBody } },
    ],
  },
];

// === Public API ===

export function startFullTour(lang: TourLang = getTourLang()): void {
  setTourLang(lang);
  setState({ active: true, segmentIndex: 0, lang });
  // Already on /? Resume immediately. Otherwise navigate, AppShell will pick up.
  if (window.location.pathname === '/') {
    runCurrentSegment();
  } else {
    window.location.assign('/');
  }
}

/** Roep aan bij elke route-change (AppShell useEffect) — als er een actieve tour is voor deze pagina, draait 'm. */
export function resumeIfActive(): void {
  const state = getState();
  if (!state?.active) return;
  const seg = SEGMENTS[state.segmentIndex];
  if (!seg) { clearState(); return; }
  if (window.location.pathname !== seg.page) return; // verkeerde pagina, wacht
  // Geef DOM een tikje tijd om volledig te renderen voor we selectors gaan zoeken
  setTimeout(() => runCurrentSegment(), 250);
}

function runCurrentSegment(): void {
  const state = getState();
  if (!state?.active) return;
  const seg = SEGMENTS[state.segmentIndex];
  if (!seg) { clearState(); return; }

  const s = STRINGS[state.lang];
  const isLast = state.segmentIndex === SEGMENTS.length - 1;
  const nextLabel = isLast ? s.done : s.nextSection;
  const steps = seg.build(s, isLast, nextLabel);

  // Laatste stap krijgt de juiste label ("Volgende sectie →" of "Klaar").
  const lastIdx = steps.length - 1;
  const originalLast = steps[lastIdx]!;
  steps[lastIdx] = {
    ...originalLast,
    popover: {
      ...(originalLast.popover ?? {}),
      nextBtnText: nextLabel,
    },
  };

  // We hooken `onNextClick` op driver-niveau ipv per-stap omdat de DriveStep-type
  // dat callback-veld niet exposed. Bij de laatste stap intercepteren we de
  // "Volgende"-klik om naar het volgende segment te navigeren ipv de tour
  // gewoon te sluiten.
  const d: Driver = driver({
    ...baseConfig(state.lang),
    steps,
    onCloseClick: () => {
      // Demo-state opruimen als gebruiker midden in de new-project tour stopt
      if (seg.page === '/project/new') getDemoBridge()?.deactivateDemo();
      clearState();
      d.destroy();
    },
    onNextClick: () => {
      const active = d.getActiveIndex() ?? 0;
      // Step-action firen (bv. demo activeren). React state heeft tijd nodig om
      // te renderen vóór de volgende step zijn DOM-selector zoekt; vandaar 200ms.
      const action = seg.stepActions?.[active];
      const advance = () => {
        if (active === lastIdx) {
          d.destroy();
          advanceToNextSegment();
        } else {
          d.moveNext();
        }
      };
      if (action) {
        action();
        setTimeout(advance, 200);
      } else {
        advance();
      }
    },
    onPrevClick: () => {
      d.movePrevious();
    },
  });
  d.drive();
}

function advanceToNextSegment(): void {
  const state = getState();
  if (!state?.active) return;
  const nextIndex = state.segmentIndex + 1;
  if (nextIndex >= SEGMENTS.length) {
    clearState();
    return;
  }
  const nextSeg = SEGMENTS[nextIndex]!;
  setState({ ...state, segmentIndex: nextIndex });
  if (window.location.pathname !== nextSeg.page) {
    window.location.assign(nextSeg.page);
  } else {
    runCurrentSegment();
  }
}

// === Per-page tour (vroegere v1, behouden voor "help me hier") ===

export function detectPageFromPath(pathname: string): 'dashboard' | 'new-project' | 'detail' | 'month' | 'ploegen' | null {
  if (pathname === '/') return 'dashboard';
  if (pathname === '/project/new') return 'new-project';
  if (pathname.startsWith('/project/')) return 'detail';
  if (pathname === '/month') return 'month';
  if (pathname === '/ploegen') return 'ploegen';
  return null;
}

// === Driver config ===

function baseConfig(lang: TourLang): Partial<Config> {
  const s = STRINGS[lang];
  return {
    showProgress: true,
    nextBtnText: s.next,
    prevBtnText: s.prev,
    doneBtnText: s.done,
    progressText: s.progress,
    overlayColor: 'rgba(15, 27, 45, 0.55)',
    allowClose: true,
    smoothScroll: true,
    popoverClass: 'recotex-tour',
  };
}
